/**
 * Consent handling shared by the form, the payload builder and the hosts.
 *
 * Server model: `PatientInfo.consents[]` of {type, statementVersion, accepted, status, takenVia,
 * takenByUserId, takenByName, takenByRoles, takenOn}. The form keeps an unsaved decision in
 * `patientDetails.consentDraft` and the payload turns it into a fresh (unstamped) record; the
 * server stamps who / roles / when and decides recorded vs pending.
 */

export const CONSENT_TYPE_CLINICAL = 'clinical-data'
export const CONSENT_STATEMENT_VERSION = 'clinical-data.v1.2026-09'

export const CONSENT_STATUS_RECORDED = 'recorded'
export const CONSENT_STATUS_PENDING = 'pending-clinical-consent'

export const CONSENT_MODE_NONE = 'none'
export const CONSENT_MODE_CLINICAL = 'clinical'
export const CONSENT_MODE_SUPPORT = 'support'

export const CONSENT_VIA_PRACTITIONER = 'practitioner-portal'
export const CONSENT_VIA_CLINIC_ASSISTANT = 'clinic-assistant-portal'
export const CONSENT_VIA_SUPPORT = 'support-portal'

/** Mode + channel from the create endpoint the host will call. */
export function deriveConsentContext(createPatientApiSignature = '', { isClinicAssistantPortal = false } = {}) {
  const signature = `${createPatientApiSignature || ''}`
  if (signature.includes('/cc/support/') || signature.includes('/support/')) {
    return { mode: CONSENT_MODE_SUPPORT, takenVia: CONSENT_VIA_SUPPORT }
  }
  if (isClinicAssistantPortal || signature.includes('/ca/') || signature.includes('/clinic-assistant/')) {
    return { mode: CONSENT_MODE_CLINICAL, takenVia: CONSENT_VIA_CLINIC_ASSISTANT }
  }
  return { mode: CONSENT_MODE_CLINICAL, takenVia: CONSENT_VIA_PRACTITIONER }
}

export function getClinicalConsent(patientDetails) {
  const list = Array.isArray(patientDetails?.consents) ? patientDetails.consents : []
  let found = null
  list.forEach((consent) => {
    if (consent && (!consent.type || consent.type === CONSENT_TYPE_CLINICAL)) found = consent
  })
  return found
}

export function isConsentRecorded(patientDetails) {
  const consent = getClinicalConsent(patientDetails)
  return Boolean(consent && consent.accepted && consent.status !== CONSENT_STATUS_PENDING)
}

export function isConsentPending(patientDetails) {
  const consent = getClinicalConsent(patientDetails)
  return Boolean(consent && (consent.status === CONSENT_STATUS_PENDING || !consent.accepted))
}

/** True when the host may submit as far as consent is concerned. */
export function isPatientConsentSatisfied(patientDetails, mode = CONSENT_MODE_NONE) {
  if (mode !== CONSENT_MODE_CLINICAL) return true
  return Boolean(patientDetails?.consentDraft?.accepted) || isConsentRecorded(patientDetails)
}

/** Fresh consent record(s) to send with the payload, or undefined when nothing new was decided. */
export function buildConsentPayload(patientDetails, { mode = CONSENT_MODE_NONE, takenVia } = {}) {
  const draft = patientDetails?.consentDraft
  if (mode === CONSENT_MODE_SUPPORT) {
    if (isConsentRecorded(patientDetails)) return undefined
    return [
      {
        type: CONSENT_TYPE_CLINICAL,
        statementVersion: CONSENT_STATEMENT_VERSION,
        accepted: false,
        takenVia: takenVia || CONSENT_VIA_SUPPORT,
        note: 'Created via support portal; clinical consent pending at the clinic.',
      },
    ]
  }
  if (draft?.accepted) {
    return [
      {
        type: CONSENT_TYPE_CLINICAL,
        statementVersion: draft.statementVersion || CONSENT_STATEMENT_VERSION,
        accepted: true,
        takenVia: draft.takenVia || takenVia || CONSENT_VIA_PRACTITIONER,
      },
    ]
  }
  return undefined
}
